import { useEffect, useRef, useState } from "react";

function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow;
}

// Tab bar with full labels. On a phone it's one horizontally scrollable
// row (no truncated 3-letter labels) and the active tab is scrolled into
// view whenever it changes; on desktop the tabs share the width.
// tabs: [{ key, label, icon? }]
const border = "rgba(0,245,255,0.15)";

export default function ScrollTabs({ tabs, active, onChange, isMobile: isMobileProp, ariaLabel, testId, style }) {
  const narrow = useNarrow();
  const isMobile = isMobileProp ?? narrow;
  const rowRef = useRef(null);
  const activeRef = useRef(null);

  // Center the active tab horizontally inside the row only — never scroll
  // the page vertically (scrollIntoView would).
  useEffect(() => {
    const row = rowRef.current;
    const btn = activeRef.current;
    if (!row || !btn || row.scrollWidth <= row.clientWidth) return;
    const left = btn.offsetLeft - (row.clientWidth - btn.offsetWidth) / 2;
    if (typeof row.scrollTo === "function") row.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    else row.scrollLeft = Math.max(0, left);
  }, [active, isMobile, tabs.length]);

  return (
    <div
      ref={rowRef}
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      className="scroll-tabs"
      style={{
        position: "relative",
        display: "flex",
        gap: "4px",
        backgroundColor: "rgba(0,245,255,0.03)",
        border: `1px solid ${border}`,
        borderRadius: "8px",
        padding: "4px",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        scrollSnapType: isMobile ? "x proximity" : undefined,
        ...style,
      }}
    >
      {tabs.map((tab) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            ref={selected ? activeRef : undefined}
            type="button"
            role="tab"
            aria-selected={selected}
            data-tab={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              flex: isMobile ? "0 0 auto" : "1 0 auto",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              minHeight: isMobile ? "44px" : "34px",
              padding: isMobile ? "0 14px" : "8px 10px",
              background: selected ? "linear-gradient(135deg, #00f5ff, #0080ff)" : "none",
              color: selected ? "#020817" : "rgba(0,245,255,0.6)",
              border: selected ? "none" : `1px solid ${isMobile ? "rgba(0,245,255,0.12)" : "transparent"}`,
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "11px",
              fontWeight: "700",
              letterSpacing: isMobile ? "0.5px" : "2px",
              whiteSpace: "nowrap",
              scrollSnapAlign: isMobile ? "center" : undefined,
            }}
          >
            {tab.icon && <span aria-hidden="true">{tab.icon}</span>}
            <span>{tab.label}</span>
          </button>
        );
      })}
      <style>{`.scroll-tabs::-webkit-scrollbar{display:none}`}</style>
    </div>
  );
}
